import React, { useEffect, useState } from 'react';
import './FavoritesTab.css';
import { apiGet, apiPost, apiDelete, ApiError } from '../utils/api';

const themealdbUrl = (externalId) => `https://www.themealdb.com/meal/${externalId}`;

const FavoritesTab = ({ currentUser }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [url, setUrl] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [preview, setPreview] = useState(null); // { title, imageUrl }
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const fetchFavorites = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiGet('/recipe-favorites', { auth: true });
        setFavorites(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [currentUser]);

  const handlePreview = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;

    setPreviewing(true);
    setPreviewError(null);
    setPreview(null);

    try {
      const result = await apiPost('/recipe-favorites/preview', { url: url.trim() }, { auth: true });
      if (!result.title && !result.imageUrl) {
        setPreviewError("Couldn't fetch a preview for that link — you can still save it with your own title below.");
      }
      setPreview({ title: result.title ?? '', imageUrl: result.imageUrl });
    } catch (err) {
      setPreviewError(
        err instanceof ApiError && err.status === 400
          ? 'That URL looks invalid — check it and try again.'
          : "Couldn't fetch a preview for that link — you can still save it with your own title below.",
      );
      setPreview({ title: '', imageUrl: null });
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!preview || !preview.title.trim()) return;

    setSaving(true);
    try {
      const created = await apiPost(
        '/recipe-favorites',
        { source: 'custom', externalId: url.trim(), title: preview.title.trim(), imageUrl: preview.imageUrl || undefined },
        { auth: true },
      );
      setFavorites((prev) => [created, ...prev]);
      setUrl('');
      setPreview(null);
      setPreviewError(null);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (favorite) => {
    try {
      await apiDelete(`/recipe-favorites/${favorite.id}`, { auth: true });
      setFavorites((prev) => prev.filter((f) => f.id !== favorite.id));
    } catch (err) {
      console.error('Error removing favorite:', err.message);
    }
  };

  if (!currentUser) {
    return (
      <div className="favorites-tab">
        <h2>Favorites</h2>
        <p className="favorites-description">Log in to save and view your favorite recipes.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading favorites...</div>;
  }

  return (
    <div className="favorites-tab">
      <h2>Favorites</h2>
      <p className="favorites-description">
        Recipes you've liked from the Recipes tab, plus any you've saved from a link.
      </p>

      {error && <div className="error">Error loading favorites: {error}</div>}

      <form onSubmit={preview ? handleSave : handlePreview} className="save-url-form">
        <div className="save-url-row">
          <input
            type="url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setPreview(null);
              setPreviewError(null);
            }}
            placeholder="Paste a recipe URL to save it..."
            className="item-input"
            required
          />
          {!preview ? (
            <button type="submit" className="btn btn-primary" disabled={previewing || !url.trim()}>
              {previewing ? 'Fetching preview...' : 'Preview'}
            </button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={saving || !preview.title.trim()}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>

        {previewError && <p className="preview-error">{previewError}</p>}

        {preview && (
          <div className="preview-card">
            {preview.imageUrl && <img src={preview.imageUrl} alt="" className="preview-image" />}
            <label className="form-label" htmlFor="preview-title">Title</label>
            <input
              id="preview-title"
              type="text"
              value={preview.title}
              onChange={(e) => setPreview((prev) => ({ ...prev, title: e.target.value }))}
              className="form-input"
              placeholder="Give this recipe a title"
              required
            />
          </div>
        )}
      </form>

      {favorites.length > 0 ? (
        <div className="recipes-grid">
          {favorites.map((favorite) => (
            <div key={favorite.id} className="recipe-card">
              {favorite.imageUrl && (
                <img src={favorite.imageUrl} alt={`${favorite.title} thumbnail`} className="recipe-image" />
              )}
              <div className="recipe-content">
                <h3>{favorite.title}</h3>
                <a
                  href={favorite.source === 'TheMealDB' ? themealdbUrl(favorite.externalId) : favorite.externalId}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-recipe-link"
                >
                  View Recipe
                </a>
                <button onClick={() => handleRemove(favorite)} className="favorite-remove-btn">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="no-recipes">You haven't saved any recipes yet.</p>
      )}
    </div>
  );
};

export default FavoritesTab;
